import { firebaseConfig } from '../config/firebase';

class NotificationService {
  private static instance: NotificationService;
  private messaging: any = null;
  private handleServiceWorkerMessage?: (event: MessageEvent) => void;
  private isInitialized = false;
  private initializationPromise: Promise<boolean> | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Initialize Firebase messaging for FCM notifications
  async initializeFirebaseMessaging() {
    try {
      // If already initialized, return the existing promise or true
      if (this.isInitialized) {
        return true;
      }

      // If initialization is in progress, return the existing promise
      if (this.initializationPromise) {
        return this.initializationPromise;
      }

      this.initializationPromise = this._initializeFirebaseMessaging();
      const result = await this.initializationPromise;
      this.isInitialized = result;
      return result;
    } catch (error) {
      this.isInitialized = false;
      return false;
    }
  }

  private async _initializeFirebaseMessaging(): Promise<boolean> {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        return false;
      }

      // Check if we're in a secure context (HTTPS or localhost)
      if (!window.isSecureContext) {
        return false;
      }

      // Import Firebase modules and config
      const { initializeApp, getApps } = await import('firebase/app');
      const { getMessaging, onMessage } = await import('firebase/messaging');

      // Check if Firebase app is already initialized
      const existingApps = getApps();
      let app;

      if (existingApps.length === 0) {
        // Initialize Firebase app
        app = initializeApp(firebaseConfig);
      } else {
        // Use existing app
        app = existingApps[0];
      }

      this.messaging = getMessaging(app);

      // Register service worker
      if ('serviceWorker' in navigator) {
        try {
          // Check if service worker is already registered
          let registration = await navigator.serviceWorker.getRegistration(
            '/firebase-messaging-sw.js'
          );

          if (!registration) {
            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
              scope: '/',
            });
          } else {
            // Update the service worker
            registration.update();
          }

          // Wait for the service worker to be ready
          await navigator.serviceWorker.ready;

          // Wait for the service worker to be active
          const installing = registration.installing;
          if (installing) {
            await new Promise((resolve) => {
              installing.addEventListener('statechange', () => {
                if (installing.state === 'installed') {
                  resolve(true);
                }
              });
            });
          } else if (registration.waiting) {
            const waiting = registration.waiting;
            await new Promise((resolve) => {
              waiting.addEventListener('statechange', () => {
                if (waiting.state === 'activated') {
                  resolve(true);
                }
              });
            });
          }
        } catch (error) {
          // Service worker registration failed silently
        }
      }

      // Request notification permission
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return false;
        }
      } else if (Notification.permission === 'denied') {
        return false;
      }

      // Listen for foreground FCM messages
      onMessage(this.messaging, (payload: any) => {
        this.showBrowserNotification(payload);
      });

      // Listen for messages from service worker
      if ('serviceWorker' in navigator) {
        // Remove any existing listeners to avoid duplicates
        if (this.handleServiceWorkerMessage) {
          navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage);
        }

        // Add the message listener
        this.handleServiceWorkerMessage = (event: MessageEvent) => {
          if (event.data && event.data.type === 'FCM_MESSAGE') {
            this.showBrowserNotification(event.data.payload);
          } else if (
            event.data &&
            event.data.type === 'DISPATCH_EVENT' &&
            event.data.eventType === 'FCM_MESSAGE'
          ) {
            this.showBrowserNotification(event.data.payload);
          } else if (
            event.data &&
            event.data.type === 'DISPATCH_CUSTOM_EVENT' &&
            event.data.eventName === 'firebase-message'
          ) {
            this.showBrowserNotification(event.data.payload);
          }
        };

        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage);
      }

      // Add global listeners
      window.addEventListener('FCM_MESSAGE', (event: any) => {
        this.showBrowserNotification(event.detail);
      });

      window.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'FCM_MESSAGE') {
          this.showBrowserNotification(event.data.payload);
        }
      });

      window.addEventListener('firebase-message', (event: any) => {
        this.showBrowserNotification(event.detail);
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  // Show browser notification from FCM payload
  private showBrowserNotification(payload: any) {
    try {
      if (!('Notification' in window)) {
        return;
      }

      // Request permission if not granted
      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            this.createNotification(payload);
          }
        });
        return;
      } else if (Notification.permission === 'granted') {
        this.createNotification(payload);
        return;
      }
      // If permission is denied or any other state, return early
      return;
    } catch (error) {
      // Silent error handling
      return;
    }
  }

  // Create and show the actual notification
  private createNotification(payload: any) {
    try {
      // Handle different payload structures
      let notificationTitle = 'New Notification';
      let notificationBody = 'You have a new notification';

      // First, try to get notification from FCM payload structure
      if (payload.notification) {
        notificationTitle = payload.notification.title || notificationTitle;
        notificationBody = payload.notification.body || notificationBody;
      } else if (payload.data) {
        // Try to get notification from data structure
        if (payload.data.message) {
          notificationBody = payload.data.message;
        } else if (payload.data.notification_data) {
          // Handle notification_data if it's a JSON string
          try {
            const notificationData =
              typeof payload.data.notification_data === 'string'
                ? JSON.parse(payload.data.notification_data)
                : payload.data.notification_data;
            if (notificationData.message) {
              notificationBody = notificationData.message;
            }
          } catch (e) {
            // Silent parsing error
          }
        } else if (payload.data.body) {
          notificationBody = payload.data.body;
        } else if (payload.data.title) {
          notificationTitle = payload.data.title;
        }

        // Check if there's a screen or type in the data
        if (payload.data.screen) {
          notificationTitle = `DES - ${payload.data.screen}`;
        }
      }

      // If we still don't have a proper body, try to extract from the full payload
      if (notificationBody === 'You have a new notification') {
        // Check if there's a message in the root or nested structure
        if (payload.message) {
          notificationBody = payload.message;
        } else if (payload.body) {
          notificationBody = payload.body;
        } else if (payload.text) {
          notificationBody = payload.text;
        }
      }

      const notificationOptions: NotificationOptions = {
        body: notificationBody,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        data: payload.data || {},
        requireInteraction: true,
        tag: 'fcm-notification-' + Date.now(), // Unique tag to avoid duplicate notifications
      };

      const notification = new Notification(notificationTitle, notificationOptions);

      // Handle notification click
      notification.onclick = () => {
        notification.close();
        // Focus the window or navigate to a specific page
        if (window.focus) {
          window.focus();
        }
      };
    } catch (error) {
      // Silent error handling
    }
  }

  // Get current FCM token
  async getCurrentFCMToken(): Promise<string | null> {
    try {
      if (!this.messaging) {
        const initialized = await this.initializeFirebaseMessaging();
        if (!initialized) {
          return null;
        }
      }

      const { getToken } = await import('firebase/messaging');
      const { vapidKey } = await import('../config/firebase');

      // Check if we have a valid messaging instance
      if (!this.messaging) {
        return null;
      }

      const token = await getToken(this.messaging, { vapidKey });
      return token || null;
    } catch (error) {
      return null;
    }
  }

  // Test notification system
  async testNotificationSystem(): Promise<boolean> {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        return false;
      }

      // Check if Firebase is initialized
      if (!this.messaging) {
        const initialized = await this.initializeFirebaseMessaging();
        if (!initialized) {
          return false;
        }
      }

      // Check if we can get a token
      const token = await this.getCurrentFCMToken();
      if (!token) {
        return false;
      }

      // Check if service worker is registered
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration(
          '/firebase-messaging-sw.js'
        );
        if (!registration || !registration.active) {
          return false;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  cleanup() {
    // Cleanup any resources if needed
    this.messaging = null;
    this.isInitialized = false;
    this.initializationPromise = null;
  }
}

export default NotificationService;
